import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography, Chip, Collapse, IconButton, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';

const fmt = (val, cur, locale) => {
  if (val == null || val === '') return null;
  const number = Number(val);
  // Evita "$NaN" en la vista previa cuando el valor aún no es numérico.
  if (Number.isNaN(number)) return null;
  return `$${number.toLocaleString(locale, { maximumFractionDigits: 0 })} ${cur}`;
};

function Meta({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}

Meta.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

/**
 * Fila de plan plegable (estilo Netflix/Spotify monocromo OTRIX). Reusable en admin y cliente:
 * - `leading`: nodo a la izquierda del summary (ej. checkbox de selección en admin).
 * - `actions`: nodo dentro del detalle expandido (ej. editar/eliminar o "Suscribirme").
 * Escala a muchos planes: cada fila gestiona su propio expand y solo monta el detalle al abrir.
 */
function PlanAccordion({
  plan,
  leading = null,
  actions = null,
  badge = null,
  defaultExpanded = false
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const priceCop = fmt(plan.price_cop, 'COP', 'es-CO');
  const priceUsd = fmt(plan.price_usd, 'USD', 'en-US');
  const types = plan.session_types || [];
  const subtitleParts = [priceCop, priceUsd].filter(Boolean);

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: '1px solid',
        borderColor: open ? 'gold.soft' : 'hairline.main',
        backgroundColor: 'surface.raised',
        boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
        overflow: 'hidden',
        transition: 'border-color 220ms cubic-bezier(0.32,0.72,0,1), transform 220ms cubic-bezier(0.32,0.72,0,1), background-color 220ms cubic-bezier(0.32,0.72,0,1)',
        '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={() => setOpen((o) => !o)}
        sx={{ px: 2, py: 1.6, cursor: 'pointer' }}>
        {leading}
        {plan.image_url ? (
          <Box
            component="img"
            src={plan.image_url}
            alt=""
            sx={{ width: 44, height: 44, borderRadius: '10px', objectFit: 'cover', border: '1px solid', borderColor: 'hairline.main', flexShrink: 0 }}
          />
        ) : (
          <Box sx={{ width: 44, height: 44, borderRadius: '10px', flexShrink: 0, display: 'grid', placeItems: 'center', backgroundColor: 'surface.overlay', border: '1px solid', borderColor: 'hairline.main' }}>
            <FitnessCenterIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, color: 'text.primary' }}>{plan.name}</Typography>
            {plan.is_subscribable && (
              <Chip
                icon={<AutorenewIcon sx={{ fontSize: 14, color: (t) => `${t.palette.gold.main} !important` }} />}
                label="Suscripción"
                size="small"
                sx={{ height: 22, backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 700, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
          </Stack>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
            {subtitleParts.length ? subtitleParts.join('   ·   ') : 'Sin precio'}
            {plan.sessions ? `   ·   ${plan.sessions} sesiones` : ''}
          </Typography>
        </Box>
        {badge}
        <IconButton
          size="small"
          aria-label={open ? 'contraer' : 'expandir'}
          sx={{ color: open ? 'gold.main' : 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)' }}>
          <ExpandMoreIcon />
        </IconButton>
      </Stack>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: 'hairline.main' }} />
        <Box sx={{ px: 2, py: 2 }}>
          {plan.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.75, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
              {plan.description}
            </Typography>
          )}
          {types.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
              {types.map((st) => (
                <Chip
                  key={st.id}
                  label={st.name}
                  size="small"
                  sx={{ backgroundColor: 'surface.overlay', color: 'text.primary', border: '1px solid', borderColor: 'hairline.main', fontWeight: 600 }}
                />
              ))}
            </Stack>
          )}
          <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: actions ? 2 : 0 }}>
            {plan.clients != null && <Meta label="Clientes" value={plan.clients} />}
            {plan.sessions != null && <Meta label="Sesiones" value={plan.sessions} />}
            {plan.subscription_interval && <Meta label="Intervalo" value={plan.subscription_interval} />}
          </Stack>
          {actions && <Box>{actions}</Box>}
        </Box>
      </Collapse>
    </Box>
  );
}

PlanAccordion.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    image_url: PropTypes.string,
    price_cop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price_usd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sessions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    clients: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_subscribable: PropTypes.bool,
    subscription_interval: PropTypes.string,
    session_types: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.any, name: PropTypes.string }))
  }).isRequired,
  leading: PropTypes.node,
  actions: PropTypes.node,
  badge: PropTypes.node,
  defaultExpanded: PropTypes.bool
};

export default PlanAccordion;
